/* eslint-disable react/no-unescaped-entities */
// ─────────────────────────────────────────────────────────────
// BuddiesHR - /best-slack-apps  (SEO listicle, redesigned)
// Ported from the Claude Design mock. Reuses the real Header/Footer
// (in the page) + FooterCTA from landing/sections, and the mascot
// APPS registry from brandLogos. Screenshots reuse the existing
// /img/best-slack-apps assets.
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { APPS } from '@buddieshr/components/brandLogos';

const SLACK_LOGO = '/img/redesign/slack-logo.png';

// ─── Data ────────────────────────────────────────────────────
const BH_APP = Object.fromEntries(APPS.map((a) => [a.id, a]));

const SUITE = [
  {
    id: 'alfy',
    role: 'Random coffee chats',
    blurb:
      'Strengthen team bonding by regularly pairing coworkers for a virtual chat. Especially useful for remote teams, where keeping natural interactions alive takes more effort.',
  },
  {
    id: 'billy',
    role: 'Birthdays & work anniversaries',
    blurb:
      'Automates birthday and work-anniversary messages so no one’s special day is forgotten, with reminders scheduled days before the event.',
  },
  {
    id: 'clappy',
    role: 'Recognition & kudos',
    blurb:
      'Send kudos and points to the people you want to celebrate. Recognition happens inside Slack, so the whole team sees great work and your company values in real time.',
  },
  {
    id: 'pulsy',
    role: 'Surveys, polls & eNPS',
    blurb:
      'Measure engagement and eNPS with lightweight surveys and polls. You can only improve what you can measure, and Pulsy makes measuring painless.',
  },
  {
    id: 'linky',
    role: 'Org chart & directory',
    blurb:
      'Lets everyone visualize the company structure and hierarchy, and keeps the org chart updated on auto-pilot, no constant context switching.',
  },
];

const CATEGORIES = [
  { id: 'engagement', label: 'Engagement & team building' },
  { id: 'communication', label: 'Communication' },
  { id: 'productivity', label: 'Productivity & PM' },
  { id: 'fun', label: 'Just for fun' },
];

// Shot id → existing screenshot in /public/img/best-slack-apps
const SHOT = {
  alfy: '/img/best-slack-apps/alfy.png',
  billy: '/img/best-slack-apps/billy.png',
  clappy: '/img/best-slack-apps/clappy.png',
  pulsy: '/img/best-slack-apps/pulsy.png',
  linky: '/img/best-slack-apps/linky.png',
  attendancebot: '/img/best-slack-apps/attendancebot.png',
  axolo: '/img/best-slack-apps/axolo_way.png',
  loom: '/img/best-slack-apps/loom.png',
  zoom: '/img/best-slack-apps/zoom.png',
  googlecal: '/img/best-slack-apps/googlecalendar.png',
  trello: '/img/best-slack-apps/trello.png',
  monday: '/img/best-slack-apps/monday.png',
  calendly: '/img/best-slack-apps/calendly.png',
  clickup: '/img/best-slack-apps/clickup.png',
  giphy: '/img/best-slack-apps/giphy.png',
};

// Third-party apps, neutral monogram tiles (we never recreate their logos),
// each paired with its product screenshot.
const THIRD_PARTY = {
  engagement: [
    {
      name: 'AttendanceBot',
      tag: 'Time off & schedules',
      mono: 'A',
      img: 'attendancebot',
      blurb:
        'The complete Slack-first solution for time off, hybrid work, time tracking and schedules. From handling leave requests to scheduling meetings and reminders, it cuts manual admin so teams can focus on real work.',
    },
  ],
  communication: [
    {
      name: 'Axolo',
      tag: 'GitHub & GitLab',
      mono: 'A',
      slash: 'PR channels',
      img: 'axolo',
      wide: true,
      blurb:
        'Brings GitHub and GitLab pull requests straight into Slack. It spins up a temporary channel for each PR and invites the author and reviewers to discuss, review and act on the code, killing the constant context switching.',
    },
    {
      name: 'Loom',
      tag: 'Async video',
      mono: 'L',
      img: 'loom',
      blurb:
        'Add clarity to a thread without booking yet another meeting. Record quick async videos directly from Slack and get your message across, context-rich, on your team’s schedule.',
    },
    {
      name: 'Zoom',
      tag: 'Meetings & calls',
      mono: 'Z',
      slash: '/zoom',
      img: 'zoom',
      blurb:
        'Start Zoom meetings and calls with crystal-clear audio and instant screen sharing from any channel, group or DM with the /zoom command. Share a Zoom Whiteboard inline, and deploy org-wide in a few steps.',
    },
  ],
  productivity: [
    {
      name: 'Google Calendar',
      tag: 'Scheduling',
      mono: 'G',
      img: 'googlecal',
      blurb: 'Keep your calendar top of mind without leaving Slack.',
      bullets: [
        'Create an event without leaving Slack',
        'Auto-sync your Slack status when you’re in a meeting',
        'View your full daily schedule, respond to invites',
        'Join Meet, Zoom, Webex or Teams calls from reminders',
      ],
    },
    {
      name: 'Trello',
      tag: 'Boards & cards',
      mono: 'T',
      slash: '/trello add',
      img: 'trello',
      blurb:
        'A collaborative board millions trust. Link Trello and Slack to add cards, change due dates and attach conversations from chat, and get rich card previews when you @Trello a channel.',
    },
    {
      name: 'Monday',
      tag: 'Work OS',
      mono: 'M',
      img: 'monday',
      blurb:
        'A flexible Work OS for running projects and processes. The Slack integration lets you create new items and post updates on existing ones without leaving Slack, with a clear view of team progress.',
    },
    {
      name: 'Calendly',
      tag: 'Booking links',
      mono: 'C',
      img: 'calendly',
      blurb:
        'Streamline meeting scheduling and get notified in Slack the moment a Calendly meeting is booked, rescheduled or cancelled, so everyone gets time back in their day.',
    },
    {
      name: 'ClickUp',
      tag: 'All-in-one tasks',
      mono: 'C',
      slash: '/clickup new',
      img: 'clickup',
      blurb:
        'Brings teams, tasks and tools together in one place. Connect ClickUp with Slack to create tasks from any channel and keep everyone up to date on progress.',
    },
  ],
};

// Framed product screenshot
function Shot({ id, alt, dark }) {
  const src = SHOT[id];
  if (!src) return null;
  return (
    <div
      style={{
        borderRadius: 12,
        overflow: 'hidden',
        border: dark ? '1px solid rgba(255,248,238,.16)' : '1px solid var(--bh-line)',
        background: '#fff',
        boxShadow: dark ? '0 8px 24px rgba(0,0,0,.28)' : 'var(--bh-shadow-sm)',
      }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={src} alt={alt} loading="lazy" style={{ display: 'block', width: '100%', height: 'auto' }} />
    </div>
  );
}

// ─── Sticky category nav (scrollspy) ─────────────────────────
function CategoryNav() {
  const [active, setActive] = useState('engagement');
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setStuck(window.scrollY > 520);
      let cur = CATEGORIES[0].id;
      for (const c of CATEGORIES) {
        const el = document.getElementById(c.id);
        if (el && el.getBoundingClientRect().top <= 160) cur = c.id;
      }
      setActive(cur);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div
      style={{
        position: 'sticky',
        top: 70,
        zIndex: 40,
        background: stuck ? 'rgba(255,248,238,.9)' : 'transparent',
        backdropFilter: stuck ? 'blur(10px)' : 'none',
        borderBottom: stuck ? '1px solid var(--bh-line)' : '1px solid transparent',
        transition: 'background .25s, border-color .25s',
      }}
    >
      <div
        className="bh-toc"
        style={{
          maxWidth: 920,
          margin: '0 auto',
          padding: '12px 40px',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
          overflowX: 'auto',
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.12em',
            color: 'var(--bh-ink-mute)',
            marginRight: 4,
            whiteSpace: 'nowrap',
          }}
        >
          Jump to
        </span>
        {CATEGORIES.map((c) => (
          <a
            key={c.id}
            href={'#' + c.id}
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              padding: '6px 14px',
              borderRadius: 999,
              border: '1.5px solid ' + (active === c.id ? 'var(--bh-ink)' : 'var(--bh-line-strong)'),
              background: active === c.id ? 'var(--bh-ink)' : 'transparent',
              color: active === c.id ? 'var(--bh-cream)' : 'var(--bh-ink-soft)',
              transition: 'all .15s',
            }}
          >
            {c.label}
          </a>
        ))}
      </div>
    </div>
  );
}

function FloatHero({ id, top, left, right, size, rot, delay }) {
  const app = BH_APP[id];
  if (!app) return null;
  return (
    <div
      className="bh-hero-float"
      style={{
        position: 'absolute',
        top,
        left,
        right,
        width: size,
        height: size,
        zIndex: 1,
        transform: `rotate(${rot}deg)`,
        animation: `bh-float 6s ease-in-out infinite`,
        animationDelay: delay,
        filter: 'drop-shadow(0 14px 22px rgba(31,27,22,.16))',
      }}
    >
      <app.Logo />
    </div>
  );
}

// ─── Hero ────────────────────────────────────────────────────
function ArticleHero() {
  return (
    <header style={{ position: 'relative', padding: '40px 40px 12px', overflow: 'hidden' }}>
      <div
        className="bh-blob"
        style={{
          position: 'absolute',
          top: -200,
          right: -160,
          width: 620,
          height: 620,
          background:
            'radial-gradient(circle at 45% 45%, var(--bh-sun-200), var(--bh-sun-100) 52%, transparent 70%)',
          zIndex: 0,
          filter: 'blur(2px)',
        }}
      />
      <FloatHero id="clappy" top="14%" right="6%" size={92} rot={-10} delay="0s" />
      <FloatHero id="alfy" top="58%" right="15%" size={70} rot={8} delay="1.1s" />
      <FloatHero id="billy" top="2%" right="24%" size={58} rot={-6} delay="0.6s" />

      <div style={{ position: 'relative', zIndex: 2, maxWidth: 920, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            fontSize: 13,
            color: 'var(--bh-ink-mute)',
            fontWeight: 600,
          }}
        >
          <span>Resources</span>
          <span>›</span>
          <span style={{ color: 'var(--bh-ink-soft)' }}>Guides</span>
        </div>
        <h1
          style={{
            fontSize: 'clamp(38px, 6vw, 76px)',
            lineHeight: 1.0,
            letterSpacing: '-0.04em',
            marginTop: 18,
            maxWidth: 820,
          }}
        >
          The best Slack apps
          <br />
          &amp; integrations,{' '}
          <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>handpicked.</span>
        </h1>
        <div
          style={{
            marginTop: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
            fontSize: 13.5,
            color: 'var(--bh-ink-mute)',
          }}
        >
          <span className="bh-chip">
            <img src={SLACK_LOGO} alt="" style={{ width: 15, height: 15 }} /> 13 apps · for Slack
          </span>
          <span>Updated June 2026</span>
          <span>·</span>
          <span>6 min read</span>
        </div>
        <p
          style={{
            fontSize: 20,
            lineHeight: 1.55,
            color: 'var(--bh-ink-soft)',
            marginTop: 24,
            maxWidth: 660,
          }}
        >
          Whether your team is remote, hybrid or in-office, Slack is where the day happens. To help
          you cut through the noise, we picked the essential apps that boost engagement, streamline
          workflows, and keep everyone aligned.
        </p>
      </div>
    </header>
  );
}

// ─── Quick answer (TL;DR) ────────────────────────────────────
function QuickAnswer() {
  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '28px 40px 8px' }}>
      <div
        style={{
          background: 'var(--bh-cream-deep)',
          borderRadius: 'var(--bh-r-lg)',
          border: '1px solid var(--bh-line)',
          padding: '28px 32px',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: 22,
          alignItems: 'start',
        }}
      >
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: 'var(--bh-sun-300)',
            border: '1.5px solid var(--bh-ink)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            flexShrink: 0,
            boxShadow: '0 3px 0 var(--bh-ink)',
          }}
        >
          ⚡
        </div>
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '.14em',
              color: 'var(--bh-sun-600)',
              marginBottom: 8,
            }}
          >
            Quick answer
          </div>
          <p style={{ fontSize: 17.5, lineHeight: 1.6, color: 'var(--bh-ink-soft)' }}>
            The best Slack apps boost engagement and productivity by streamlining workflows,
            improving communication and building culture. Our top picks: the{' '}
            <strong style={{ color: 'var(--bh-ink)' }}>BuddiesHR suite</strong> for recognition,
            surveys and coffee chats, plus tools like{' '}
            <strong style={{ color: 'var(--bh-ink)' }}>Google Calendar, Trello and Loom</strong> for
            seamless collaboration.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Section heading ─────────────────────────────────────────
function SectionHead({ id, kicker, title, sub }) {
  return (
    <div id={id} style={{ maxWidth: 920, margin: '0 auto', padding: '64px 40px 8px', scrollMarginTop: 120 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '.14em',
          color: 'var(--bh-ink-mute)',
          marginBottom: 12,
        }}
      >
        {kicker}
      </div>
      <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', letterSpacing: '-0.03em', lineHeight: 1.05, maxWidth: 720 }}>
        {title}
      </h2>
      {sub && (
        <p style={{ fontSize: 17, color: 'var(--bh-ink-soft)', marginTop: 14, maxWidth: 620, lineHeight: 1.55 }}>
          {sub}
        </p>
      )}
    </div>
  );
}

// ─── BuddiesHR suite feature (the #1 pick) ───────────────────
function SuiteFeature() {
  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '28px 40px 0' }}>
      <div
        style={{
          position: 'relative',
          borderRadius: 'var(--bh-r-xl)',
          overflow: 'hidden',
          background: 'var(--bh-ink)',
          color: 'var(--bh-cream)',
          padding: '40px 40px 44px',
          boxShadow: 'var(--bh-shadow-lg)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: -120,
            right: -80,
            width: 420,
            height: 420,
            borderRadius: '50%',
            background:
              'radial-gradient(circle at 40% 40%, rgba(255,159,28,.5), rgba(255,127,92,.18) 55%, transparent 72%)',
          }}
        />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                background: 'var(--bh-sun-300)',
                color: 'var(--bh-ink)',
                fontSize: 12.5,
                fontWeight: 700,
                padding: '5px 13px',
                borderRadius: 999,
                letterSpacing: '.02em',
                whiteSpace: 'nowrap',
              }}
            >
              ★ Editor’s pick
            </span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'rgba(255,248,238,.6)' }}>
              #1 · Made by us, native in Slack
            </span>
          </div>
          <h3
            style={{
              fontFamily: 'var(--bh-font-display)',
              fontSize: 'clamp(28px, 4vw, 40px)',
              letterSpacing: '-0.025em',
              lineHeight: 1.08,
              marginTop: 18,
              color: 'var(--bh-cream)',
            }}
          >
            The BuddiesHR suite of engagement apps
          </h3>
          <p
            style={{
              fontSize: 17,
              lineHeight: 1.6,
              color: 'rgba(255,248,238,.75)',
              marginTop: 14,
              maxWidth: 600,
            }}
          >
            Several culture apps, fully integrated in Slack. Pick one, or get the whole bundle at a
            packaged price and supercharge your culture, from $1/user/month.
          </p>

          <div
            className="bh-suite-grid"
            style={{ marginTop: 30, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}
          >
            {SUITE.map((s) => {
              const app = BH_APP[s.id];
              return (
                <div
                  key={s.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'rgba(255,248,238,.06)',
                    border: '1px solid rgba(255,248,238,.12)',
                    borderRadius: 'var(--bh-r-lg)',
                    padding: 14,
                    gap: 14,
                  }}
                >
                  <Shot id={s.id} alt={app.name + ' running in Slack'} dark />
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 12, alignItems: 'center' }}>
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        flexShrink: 0,
                        borderRadius: 12,
                        overflow: 'hidden',
                        background: app.bg,
                      }}
                    >
                      <app.Logo />
                    </div>
                    <div>
                      <div
                        style={{
                          fontFamily: 'var(--bh-font-display)',
                          fontSize: 19,
                          fontWeight: 600,
                          letterSpacing: '-0.02em',
                          color: 'var(--bh-cream)',
                          lineHeight: 1.1,
                        }}
                      >
                        {app.name}
                      </div>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--bh-sun-300)', marginTop: 2 }}>
                        {s.role}
                      </div>
                    </div>
                  </div>
                  <p style={{ fontSize: 13.5, lineHeight: 1.5, color: 'rgba(255,248,238,.7)', margin: 0 }}>
                    {s.blurb}
                  </p>
                </div>
              );
            })}
            <Link
              href="/get-started"
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 8,
                background: 'rgba(255,159,28,.12)',
                border: '1px dashed rgba(255,159,28,.5)',
                borderRadius: 'var(--bh-r-lg)',
                padding: '18px 20px',
                textDecoration: 'none',
                color: 'var(--bh-cream)',
              }}
            >
              <div style={{ display: 'flex', gap: 6 }}>
                {['palmy', 'stany'].map((id) => (
                  <div key={id} style={{ width: 34, height: 34, borderRadius: 10, overflow: 'hidden', background: BH_APP[id].bg }}>
                    {React.createElement(BH_APP[id].Logo)}
                  </div>
                ))}
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: 'var(--app-perf-bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                  }}
                >
                  🎯
                </div>
              </div>
              <div style={{ fontFamily: 'var(--bh-font-display)', fontSize: 18, fontWeight: 600, letterSpacing: '-0.02em' }}>
                Palmy, Stany &amp; SimplePerf
              </div>
              <div style={{ fontSize: 13, color: 'var(--bh-sun-300)', fontWeight: 600 }}>See the full suite <span className="bh-arrow">→</span></div>
            </Link>
          </div>

          <div style={{ marginTop: 30, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <Link
              href="/slack"
              className="bh-btn primary on-dark"
              style={{
                textDecoration: 'none',
              }}
            >
              <img src={SLACK_LOGO} alt="" style={{ width: 19, height: 19, marginRight: 7, verticalAlign: '-4px' }} /> Start free in Slack
            </Link>
            <Link
              href="/pricing"
              style={{
                fontSize: 14.5,
                fontWeight: 600,
                color: 'rgba(255,248,238,.7)',
                textDecoration: 'underline',
                textUnderlineOffset: 4,
                textDecorationColor: 'rgba(255,248,238,.3)',
              }}
            >
              See bundle pricing <span className="bh-arrow">→</span>
            </Link>
            <span style={{ fontSize: 13, color: 'rgba(255,248,238,.5)', marginLeft: 'auto' }}>
              ⭐ 4.8 on G2 · Free under 10 employees
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Third-party app card (with product screenshot) ──────────
function AppCard({ rank, name, tag, blurb, bullets, slash, mono, img, wide }) {
  const head = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div
        style={{
          width: 52,
          height: 52,
          flexShrink: 0,
          borderRadius: 14,
          background: 'var(--bh-cream-deep)',
          border: '1px solid var(--bh-line-strong)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--bh-font-display)',
          fontSize: 24,
          fontWeight: 600,
          color: 'var(--bh-ink)',
        }}
      >
        {mono}
      </div>
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--bh-ink-mute)' }}>{rank}</span>
          <span style={{ fontFamily: 'var(--bh-font-display)', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>
            {name}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--bh-ink-soft)',
              background: 'var(--bh-cream-deep)',
              border: '1px solid var(--bh-line)',
              padding: '3px 10px',
              borderRadius: 999,
              whiteSpace: 'nowrap',
            }}
          >
            {tag}
          </span>
          {slash && (
            <span
              style={{
                fontFamily: 'var(--bh-font-mono)',
                fontSize: 12,
                color: 'var(--bh-plum-400)',
                background: 'rgba(94,60,131,.08)',
                padding: '3px 9px',
                borderRadius: 7,
                whiteSpace: 'nowrap',
              }}
            >
              {slash}
            </span>
          )}
        </div>
      </div>
    </div>
  );
  const body = (
    <div>
      <p style={{ fontSize: 15.5, lineHeight: 1.6, color: 'var(--bh-ink-soft)', marginTop: 14 }}>{blurb}</p>
      {bullets && (
        <ul style={{ margin: '12px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: 7 }}>
          {bullets.map((b, i) => (
            <li
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: 10,
                fontSize: 14.5,
                color: 'var(--bh-ink-soft)',
                lineHeight: 1.5,
              }}
            >
              <span style={{ color: 'var(--bh-sun-500)', fontWeight: 700 }}>›</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  const cardStyle = {
    background: 'var(--bh-surface)',
    border: '1px solid var(--bh-line)',
    borderRadius: 'var(--bh-r-lg)',
    padding: '24px 26px',
    boxShadow: 'var(--bh-shadow-sm)',
  };
  if (wide || !img) {
    return (
      <div style={cardStyle}>
        {head}
        {body}
        {img && (
          <div style={{ marginTop: 18 }}>
            <Shot id={img} alt={name + ' in Slack'} />
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="bh-app-card" style={{ ...cardStyle, display: 'grid', gridTemplateColumns: '1fr 340px', gap: 26, alignItems: 'center' }}>
      <div>
        {head}
        {body}
      </div>
      <Shot id={img} alt={name + ' in Slack'} />
    </div>
  );
}

function CardList({ items, startRank = 2 }) {
  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '20px 40px 0', display: 'grid', gap: 16 }}>
      {items.map((it, i) => (
        <AppCard key={it.name} rank={'#' + (startRank + i)} {...it} />
      ))}
    </div>
  );
}

// ─── Bonus / fun ─────────────────────────────────────────────
function BonusSection() {
  return (
    <div id="fun">
      <SectionHead kicker="Bonus" title="…and one just for fun" sub="Because culture isn’t all surveys and standups." />
      <div style={{ maxWidth: 920, margin: '0 auto', padding: '20px 40px 0' }}>
        <div
          className="bh-app-card"
          style={{
            background: 'linear-gradient(100deg, var(--app-stany-bg), var(--bh-cream-deep))',
            border: '1px solid var(--bh-line)',
            borderRadius: 'var(--bh-r-lg)',
            padding: '24px 26px',
            display: 'grid',
            gridTemplateColumns: '1fr 320px',
            gap: 26,
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--bh-font-display)', fontSize: 23, fontWeight: 600, letterSpacing: '-0.02em' }}>
                Giphy
              </span>
              <span
                style={{
                  fontFamily: 'var(--bh-font-mono)',
                  fontSize: 12,
                  color: 'var(--bh-plum-400)',
                  background: 'rgba(94,60,131,.08)',
                  padding: '3px 9px',
                  borderRadius: 7,
                }}
              >
                /giphy
              </span>
            </div>
            <p style={{ fontSize: 15.5, lineHeight: 1.6, color: 'var(--bh-ink-soft)', marginTop: 10 }}>
              A must-have for every workspace. Slack added native GIFs, but Giphy is still the
              fastest way to drop the perfect reaction. Type{' '}
              <code style={{ fontFamily: 'var(--bh-font-mono)', fontSize: 13, background: 'rgba(31,27,22,.06)', padding: '1px 6px', borderRadius: 5 }}>
                /giphy cats
              </code>{' '}
              and a cat GIF lands in your channel, a little fun that keeps teamwork and culture
              going hand in hand.
            </p>
          </div>
          <Shot id="giphy" alt="Giphy in Slack" />
        </div>
      </div>
    </div>
  );
}

// ─── More resources ──────────────────────────────────────────
function Resources() {
  const links = [
    {
      label: 'Virtual team building for remote teams',
      meta: 'Guide',
      href: '/virtual-team-building-remote-teams',
    },
    {
      label: 'Best Slack poll & survey apps: SimplePoll vs Polly vs Pulsy',
      meta: 'Comparison · 2026',
      href: '/alternatives/simplepoll-polly-pulsy',
    },
  ];
  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '64px 40px 0' }}>
      <h2 style={{ fontSize: 'clamp(24px, 3.4vw, 34px)', letterSpacing: '-0.03em' }}>Keep reading</h2>
      <div className="bh-res-grid" style={{ marginTop: 22, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {links.map((l) => (
          <Link
            key={l.label}
            href={l.href}
            style={{
              display: 'block',
              background: 'var(--bh-surface)',
              border: '1px solid var(--bh-line)',
              borderRadius: 'var(--bh-r-lg)',
              padding: '22px 24px',
              textDecoration: 'none',
              color: 'var(--bh-ink)',
            }}
          >
            <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--bh-sun-600)' }}>
              {l.meta}
            </div>
            <div style={{ fontFamily: 'var(--bh-font-display)', fontSize: 19, fontWeight: 600, letterSpacing: '-0.02em', marginTop: 8, lineHeight: 1.25 }}>
              {l.label}
            </div>
            <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600, color: 'var(--bh-ink-soft)' }}>Read <span className="bh-arrow">→</span></div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── What is a Slack app? ────────────────────────────────────
function WhatIs() {
  const qa = [
    {
      q: 'What is a Slack app?',
      a: 'Slack apps are tools you install on your workspace to add functionality, for yourself or the whole team. They streamline your workday and improve your work experience, across categories like team building, communication, productivity, project management and culture.',
    },
    {
      q: 'What does a Slack bot actually do?',
      a: 'Slack bots save time by handling repetitive tasks and bringing shortcuts right into Slack, so you act on things in chat instead of opening yet another website.',
    },
    {
      q: 'Where do I find more apps?',
      a: 'The full catalog lives in the Slack App Directory. Start with the few that solve a real problem for your team, then add more as you go, every app here installs in a couple of clicks.',
    },
  ];
  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '64px 40px 0' }}>
      <h2 style={{ fontSize: 'clamp(24px, 3.4vw, 34px)', letterSpacing: '-0.03em' }}>Slack apps, explained</h2>
      <div style={{ marginTop: 22, display: 'grid', gap: 12 }}>
        {qa.map((item) => (
          <div key={item.q} style={{ background: 'var(--bh-cream-deep)', border: '1px solid var(--bh-line)', borderRadius: 'var(--bh-r-md)', padding: '22px 26px' }}>
            <div style={{ fontFamily: 'var(--bh-font-display)', fontSize: 19, fontWeight: 600, letterSpacing: '-0.015em' }}>{item.q}</div>
            <p style={{ fontSize: 15.5, lineHeight: 1.6, color: 'var(--bh-ink-soft)', marginTop: 10 }}>{item.a}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Article (everything except Header/Footer) ───────────────
export function BestSlackAppsArticle() {
  return (
    <>
      <ArticleHero />
      <CategoryNav />
      <QuickAnswer />

      <SectionHead
        id="engagement"
        kicker="Category 01"
        title="The best Slack apps for engagement & team building"
        sub="The apps that make work feel human: recognition, celebrations, surveys and connection, delivered where your team already is."
      />
      <SuiteFeature />
      <CardList items={THIRD_PARTY.engagement} startRank={2} />

      <SectionHead
        id="communication"
        kicker="Category 02"
        title="The best Slack apps for communication & collaboration"
        sub="Bring code reviews, video and meetings into the conversation, and out of yet another browser tab."
      />
      <CardList items={THIRD_PARTY.communication} startRank={1} />

      <SectionHead
        id="productivity"
        kicker="Category 03"
        title="The best Slack apps for productivity & project management"
        sub="Turn messages into tasks, events and updates without ever leaving the workspace."
      />
      <CardList items={THIRD_PARTY.productivity} startRank={1} />

      <BonusSection />
      <Resources />
      <WhatIs />

      <div style={{ height: 64 }} />
    </>
  );
}
